import { LEAD_STATUS, LEAD_STATUS_LABELS, LEAD_STATUS_COLORS, canonicalLeadStatus } from "./constants";

/**
 * Proceso de venta simplificado.
 * Los códigos enviados al backend siguen siendo los enums existentes;
 * aquí se agrupan y se muestran como un flujo único.
 */
export const PIPELINE_STAGES = [
  {
    key: "nuevo",
    label: "Nuevo",
    subtitle: "Alta sin contacto",
    expectedRate: 10,
    accent: "#334155",
    gradient: "linear-gradient(135deg, #475569 0%, #334155 100%)",
    glow: "rgba(100, 116, 139, 0.25)",
    statuses: [LEAD_STATUS.PENDING_CONTACT, LEAD_STATUS.NO_INFO],
  },
  {
    key: "contacting",
    label: "Contactando",
    subtitle: "Primer acercamiento",
    expectedRate: 25,
    accent: "#2563eb",
    gradient: "linear-gradient(135deg, #3b82f6 0%, #2563eb 100%)",
    glow: "rgba(37, 99, 235, 0.3)",
    statuses: [
      LEAD_STATUS.CONTACTED,
      LEAD_STATUS.VOICEMAIL,
      LEAD_STATUS.CALL_BACK,
      LEAD_STATUS.INFO_SENT,
    ],
  },
  {
    key: "qualified",
    label: "Calificado",
    subtitle: "Interés confirmado",
    expectedRate: 45,
    accent: "#0f766e",
    gradient: "linear-gradient(135deg, #14b8a6 0%, #0f766e 100%)",
    glow: "rgba(13, 148, 136, 0.3)",
    statuses: [LEAD_STATUS.IN_NEGOTIATION],
  },
  {
    key: "demo",
    label: "Demo",
    subtitle: "Presentación agendada",
    expectedRate: 60,
    accent: "#7c3aed",
    gradient: "linear-gradient(135deg, #8b5cf6 0%, #7c3aed 100%)",
    glow: "rgba(124, 58, 237, 0.3)",
    statuses: [LEAD_STATUS.APPOINTMENT_SET],
  },
  {
    key: "closing",
    label: "Cierre",
    subtitle: "Propuesta en juego",
    expectedRate: 80,
    accent: "#b45309",
    gradient: "linear-gradient(135deg, #f59e0b 0%, #b45309 100%)",
    glow: "rgba(245, 158, 11, 0.3)",
    statuses: [LEAD_STATUS.PROPOSAL_SENT, LEAD_STATUS.AWAITING_RESPONSE],
  },
  {
    key: "won",
    label: "Ganado",
    subtitle: "Venta cerrada",
    expectedRate: 100,
    accent: "#047857",
    gradient: "linear-gradient(135deg, #22c55e 0%, #047857 100%)",
    glow: "rgba(34, 197, 94, 0.3)",
    statuses: [LEAD_STATUS.CURRENT_CLIENT],
  },
  {
    key: "lost",
    label: "Perdido",
    subtitle: "Sin oportunidad",
    expectedRate: 0,
    accent: "#ef4444",
    gradient: "linear-gradient(135deg, #f87171 0%, #ef4444 100%)",
    glow: "rgba(239, 68, 68, 0.2)",
    statuses: [
      LEAD_STATUS.NOT_INTERESTED,
      LEAD_STATUS.NO_RESPONSE,
      LEAD_STATUS.OUT_OF_SERVICE,
    ],
  },
];

export const FORWARD_STAGE_KEYS = ["nuevo", "contacting", "qualified", "demo", "closing", "won"];
export const LOST_STAGE_KEY = "lost";
export const FIRST_STAGE_KEY = "nuevo";

const STAGE_BY_KEY = Object.fromEntries(PIPELINE_STAGES.map((s) => [s.key, s]));

export function getStageForStatus(status) {
  const canonical = canonicalLeadStatus(status);
  return PIPELINE_STAGES.find((s) => s.statuses.includes(canonical) || s.statuses.includes(status))
    ?? PIPELINE_STAGES[0];
}

export function getStageForLead(lead) {
  if (lead?.outcome === "WON") return STAGE_BY_KEY.won;
  if (lead?.outcome === "LOST" || lead?.outcome === "PAUSED") return STAGE_BY_KEY.lost;
  return getStageForStatus(lead?.status);
}

export function groupLeadsByStage(leads = []) {
  const groups = Object.fromEntries(PIPELINE_STAGES.map((s) => [s.key, []]));
  for (const lead of leads) {
    groups[getStageForLead(lead).key].push(lead);
  }
  for (const key of Object.keys(groups)) {
    groups[key].sort((a, b) => (b.priority === "URGENT") - (a.priority === "URGENT"));
  }
  return groups;
}

export function statusLabel(status) {
  return LEAD_STATUS_LABELS[canonicalLeadStatus(status)] || LEAD_STATUS_LABELS[status] || status;
}

export function statusColor(status) {
  const canonical = canonicalLeadStatus(status);
  return LEAD_STATUS_COLORS[canonical] || LEAD_STATUS_COLORS[status] || "#6b7280";
}
